import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, Send, RefreshCw, CheckCircle, Lightbulb, Bug, Wrench, Star, List, Check, Archive, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";

const FEEDBACK_TYPES = [
  { value: "feature", label: "Feature Request", icon: Lightbulb, color: "text-yellow-400" },
  { value: "bug", label: "Bug in BugNexus", icon: Bug, color: "text-red-400" },
  { value: "improvement", label: "Improvement", icon: Wrench, color: "text-blue-400" },
  { value: "other", label: "Other", icon: Star, color: "text-purple-400" },
];

export default function FeedbackSection() {
  const { user, isAuthenticated } = useAuth();
  const [feedbackType, setFeedbackType] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Feedback log state (for admins)
  const [feedbackList, setFeedbackList] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [activeTab, setActiveTab] = useState("submit");
  
  const isAdmin = isAuthenticated && user?.role?.toLowerCase() === 'admin';

  // Fetch feedback log for admins
  const fetchFeedbackLog = async () => {
    if (!isAdmin) return;
    
    try {
      setLoadingFeedback(true);
      const response = await axios.get(`${API}/feedback`, { withCredentials: true });
      setFeedbackList(response.data || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      if (error.response?.status !== 403) {
        toast.error("Failed to load feedback log");
      }
    } finally {
      setLoadingFeedback(false);
    }
  };

  useEffect(() => {
    if (isAdmin && activeTab === "log") {
      fetchFeedbackLog();
    }
  }, [isAdmin, activeTab]);

  const handleFeedbackStatus = async (feedbackId, status) => {
    try {
      await axios.put(`${API}/feedback/${feedbackId}/status`, { status }, { withCredentials: true });
      toast.success(`Feedback marked as ${status}`);
      fetchFeedbackLog();
    } catch (error) {
      console.error("Error updating feedback:", error);
      toast.error("Failed to update feedback");
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm("Delete this feedback? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/feedback/${feedbackId}`, { withCredentials: true });
      toast.success("Feedback deleted");
      fetchFeedbackLog();
    } catch (error) {
      console.error("Error deleting feedback:", error);
      toast.error("Failed to delete feedback");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackType) {
      toast.error("Please select a feedback type");
      return;
    }
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/feedback`, {
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        contact_email: contactEmail.trim() || null,
      });
      
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFeedbackType("");
        setTitle("");
        setDescription("");
        setContactEmail("");
      }, 3000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-4 md:p-8 animate-fade-in" data-testid="feedback-section">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-card border-border/40 border-l-4 border-l-emerald-500">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold uppercase tracking-wider text-foreground mb-2">
                Feedback Submitted!
              </h2>
              <p className="text-muted-foreground font-body">
                Thank you for helping us improve BugNexus. We review all feedback and will consider your suggestions for future updates.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="feedback-section">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">
          BugNexus Feedback
        </h1>
        <div className="cyber-line mt-2" />
        <p className="text-muted-foreground mt-2 font-body text-sm md:text-base">
          Help us improve BugNexus! Share your ideas, report issues, or suggest new features.
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Admin Tabs */}
        {isAdmin ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full rounded-none border-b border-border/40 bg-muted/20 p-0 h-auto mb-6">
              <TabsTrigger
                value="submit"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Feedback
              </TabsTrigger>
              <TabsTrigger
                value="log"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
              >
                <List className="w-4 h-4 mr-2" />
                Feedback Log ({feedbackList.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="submit" className="m-0">
              {renderSubmitForm()}
            </TabsContent>

            <TabsContent value="log" className="m-0">
              {renderFeedbackLog()}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="max-w-2xl mx-auto">
            {renderSubmitForm()}
          </div>
        )}
      </div>
    </div>
  );

  function renderFeedbackLog() {
    if (loadingFeedback) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    if (feedbackList.length === 0) {
      return (
        <Card className="bg-card border-border/40">
          <CardContent className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground font-mono">No feedback received yet</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground font-mono">
            {feedbackList.length} feedback item{feedbackList.length !== 1 ? 's' : ''}
          </p>
          <Button variant="outline" size="sm" onClick={fetchFeedbackLog} className="font-mono text-xs">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {feedbackList.map((f) => {
          const typeInfo = FEEDBACK_TYPES.find(t => t.value === f.type) || FEEDBACK_TYPES[3];
          const TypeIcon = typeInfo.icon;
          
          return (
            <Card key={f.id} className={`bg-card border-border/40 ${f.status === 'handled' ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm font-mono uppercase ${
                        f.type === 'feature' ? 'bg-yellow-500/20 text-yellow-400' :
                        f.type === 'bug' ? 'bg-red-500/20 text-red-400' :
                        f.type === 'improvement' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-purple-500/20 text-purple-400'
                      }`}>
                        <TypeIcon className="w-3 h-3" />
                        {typeInfo.label}
                      </span>
                      {f.status === 'handled' && (
                        <span className="text-xs px-2 py-0.5 rounded-sm font-mono uppercase bg-green-500/20 text-green-400">
                          Handled
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground font-mono flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(f.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-foreground mb-1 truncate">{f.title}</h3>
                    <p className="text-sm text-muted-foreground font-body line-clamp-2">{f.description}</p>
                    {f.contact_email && (
                      <p className="text-xs text-primary font-mono mt-2">Contact: {f.contact_email}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {f.status !== 'handled' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleFeedbackStatus(f.id, 'handled')}
                        className="text-xs font-mono text-green-400 border-green-500/30 hover:bg-green-500/10"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Handled
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleFeedbackStatus(f.id, 'pending')}
                        className="text-xs font-mono text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10"
                      >
                        <Archive className="w-3 h-3 mr-1" />
                        Reopen
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteFeedback(f.id)}
                      className="text-xs font-mono text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  function renderSubmitForm() {
    return (
      <>
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Submit Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Feedback Type */}
              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                  Feedback Type *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {FEEDBACK_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFeedbackType(type.value)}
                        className={`p-3 rounded-sm border transition-all text-center ${
                          feedbackType === type.value
                            ? "bg-primary/20 border-primary/50"
                            : "bg-black/20 border-border/40 hover:border-border"
                        }`}
                        data-testid={`feedback-type-${type.value}`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${type.color}`} />
                        <span className="text-xs font-mono">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                  Title *
                </label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Brief summary of your feedback"
                  className="bg-black/20 border-border/50 focus:border-primary"
                  data-testid="feedback-title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                  Description *
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe your feedback in detail. For feature requests, explain the use case. For bugs, include steps to reproduce."
                  className="min-h-[150px] bg-black/20 border-border/50 focus:border-primary"
                  data-testid="feedback-description"
                />
              </div>

              {/* Contact Email (Optional) */}
              <div>
                <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                  Contact Email (Optional)
                </label>
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@email.com - if you'd like us to follow up"
                  className="bg-black/20 border-border/50 focus:border-primary"
                  data-testid="feedback-email"
                />
                <p className="text-[10px] text-muted-foreground font-mono mt-1">
                  We'll only use this to follow up on your feedback
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full font-heading font-bold uppercase tracking-wider py-6"
                data-testid="submit-feedback-btn"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Feedback Guidelines */}
        <Card className="bg-card border-border/40 mt-6">
          <CardContent className="p-4">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Feedback Guidelines
            </h3>
            <ul className="space-y-2 text-xs text-muted-foreground font-body">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong>Feature Requests:</strong> Describe the problem you're trying to solve and how the feature would help.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                <span><strong>Bug Reports:</strong> Include steps to reproduce, expected vs actual behavior, and any error messages.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400">•</span>
                <span><strong>Improvements:</strong> Explain what could be better and how it would enhance the user experience.</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </>
    );
  }
}
